/* =====================================================
   MY RECIPES PAGE — JavaScript Calculator
   File: assets/my-recipes.js
   ===================================================== */

(function () {
  "use strict";

  /* ── Modal: Calorie distribution info ─────────────── */
  function initMacroInfoModal() {
    const overlay = document.querySelector("[data-macro-info-overlay]");
    const modal = document.getElementById("macro-info-modal");
    if (!overlay || !modal) return;

    let lastActiveEl = null;

    function isOpen() {
      return !overlay.hasAttribute("hidden");
    }

    function open() {
      if (isOpen()) return;
      lastActiveEl = document.activeElement;
      overlay.removeAttribute("hidden");
      window.requestAnimationFrame(function () {
        modal.focus();
      });
    }

    function close() {
      if (!isOpen()) return;
      overlay.setAttribute("hidden", "");
      const el = lastActiveEl;
      lastActiveEl = null;
      if (el && typeof el.focus === "function") el.focus();
    }

    document.addEventListener("click", function (e) {
      const btn =
        e.target && e.target.closest
          ? e.target.closest("[data-macro-info-open]")
          : null;
      if (!btn) return;
      e.preventDefault();
      const card = btn.closest(".recipe-card");
      if (card) {
        initMacroChart(card);
      }
      open();
    });

    overlay.addEventListener("click", function (e) {
      const closeBtn =
        e.target && e.target.closest
          ? e.target.closest("[data-macro-info-close]")
          : null;
      if (closeBtn) {
        e.preventDefault();
        close();
        return;
      }
      if (e.target === overlay) close();
    });

    document.addEventListener("keydown", function (e) {
      if (!isOpen()) return;
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
    });
  }

  /* ── Formatting helpers ───────────────────────────── */

  function fmtGrams(g) {
    return g + "\u202fg";
  }

  function fmtGBP(amount) {
    const n = typeof amount === "number" ? amount : parseFloat(amount);
    if (!Number.isFinite(n)) return "";
    return "~ \u00a3" + n.toFixed(2);
  }

  function costFromGrams(grams, costPerKg) {
    const g = typeof grams === "number" ? grams : parseFloat(grams);
    const c = typeof costPerKg === "number" ? costPerKg : parseFloat(costPerKg);
    if (!Number.isFinite(g) || !Number.isFinite(c) || c <= 0) return null;
    return (g / 1000) * c;
  }

  function gramsToDisplayAmount(grams, unit) {
    const u = (unit || "g").toLowerCase();
    if (u === "g") return grams;
    if (u === "drop") return grams;
    if (u === "kg") return grams / 1000;
    if (u === "tsp") return grams / 5;
    if (u === "tbsp") return grams / 15;
    if (u === "cup") return grams / 240;
    return grams;
  }

  function _fracStr(whole, frac) {
    const FRACS = ["", "¼", "½", "¾"];
    const fi = Math.round(frac * 4);
    if (fi >= 4) return String(whole + 1);
    return (whole > 0 ? String(whole) : "") + FRACS[fi];
  }

  function fmtQtyFromGrams(grams, unit) {
    const u = (unit || "g").toLowerCase();
    if (u === "kg") {
      const kg = gramsToDisplayAmount(grams, "kg");
      const kgStr = (kg >= 10 ? kg.toFixed(1) : kg.toFixed(2)).replace(
        /\.0+$/,
        "",
      );
      return kgStr + "\u202fkg";
    }
    if (!u || u === "g") return fmtGrams(Math.round(grams));
    if (u === "drop") {
      const n = Math.round(grams);
      return n + "\u202f" + (n === 1 ? "drop" : "drops");
    }
    if (u === "tsp" || u === "tbsp" || u === "cup") {
      const amt = gramsToDisplayAmount(grams, u);
      const whole = Math.floor(amt);
      const frac = amt - whole;
      return _fracStr(whole, frac) + "\u202f" + (u || "tsp");
    }
    return fmtGrams(Math.round(grams));
  }

  function daysLabel(d) {
    const T = window.RECIPE_PAGE_TRANSLATIONS || {};
    return d + "\u202f" + (d === 1 ? T.day || "day" : T.days || "days");
  }

  /* ── Feed type helpers ────────────────────────────── */

  function readFeedType(card) {
    const active = card.querySelector(".feed-type-btn--active");
    return active ? parseInt(active.dataset.value, 10) : null;
  }

  function readCommercialKcal(card) {
    const input = card.querySelector('[name="commercial_kcal"]');
    return input ? parseFloat(input.value) || 0 : 0;
  }

  /* ── Treats helpers ───────────────────────────────── */
  function readTreats(card) {
    const active = card.querySelector(".treats-btn--active");
    return active ? active.dataset.treats : null; // 'yes' | 'no' | null
  }
  function readTreatsPct(card) {
    return readTreats(card) === "yes" ? 10 : 0;
  }

  function clearTreats(card) {
    card.querySelectorAll(".treats-btn").forEach(function (btn) {
      btn.classList.remove("treats-btn--active");
      btn.setAttribute("aria-pressed", "false");
    });
  }

  function renderMer(card, scope) {
    const withTreats = scope.treats_pct > 0;
    const label = card.querySelector("[data-mer-label]");
    const merInput = card.querySelector('[name="mer"]');
    const treatField = card.querySelector(".calc-field--mer-treats");
    const treatInput = card.querySelector('[name="mer_treats"]');

    const foodKcal = Math.round(scope.food_mer);
    const treatKcal = Math.round(scope.full_mer) - foodKcal; // keeps the two figures summing to the total

    if (label)
      label.textContent = withTreats
        ? "MER (90% daily kcal from main meals)"
        : "MER (100% daily kcal from main meals)";
    if (merInput)
      merInput.value = Number.isFinite(foodKcal) ? String(foodKcal) : "";

    if (treatInput)
      treatInput.value =
        withTreats && Number.isFinite(treatKcal) ? String(treatKcal) : "";
    if (treatField) {
      if (withTreats) reveal(treatField);
      else hide(treatField);
    }
  }

  /* ── Qty header label ─────────────────────────────── */
  function updateQtyHeader(recipeId, days) {
    const el = document.getElementById("qty-header-" + recipeId);
    if (el) el.textContent = daysLabel(days) + " amount";
  }

  /* ── Flash animation on qty cells ────────────────── */
  function flashQtyCells(container) {
    if (!container) return;
    const cells = container.querySelectorAll(".ingredient-row__qty");
    cells.forEach(function (cell) {
      cell.classList.remove("flash");
      void cell.offsetWidth;
      cell.classList.add("flash");
    });
  }

  /* ── Build variable scope ─────────────────────────── */
  function buildScope(
    dogWeight,
    activityLevel,
    baseKcalPerDay,
    kcalPerKg,
    days,
    homemadePct,
    treatsPct,
  ) {
    const level = (activityLevel || "").trim();
    const factor =
      level === "More active / higher energy"
        ? 110
        : level === "Less active / low energy" ||
            level === "Less active / lower energy"
          ? 95
          : 95;

    const treatShare = (treatsPct || 0) / 100;
    const totalMer = factor * Math.pow(dogWeight, 0.75);
    const foodMer = totalMer * (1 - treatShare); // kcal from main meals
    const treatMer = totalMer - foodMer; // kcal from treats
    const effectiveMer = foodMer * ((homemadePct || 100) / 100); // homemade share of the food kcal
    const scalingFactor = effectiveMer / baseKcalPerDay;

    return {
      dog_weight: dogWeight,
      activity_level: level,
      days: days,
      base_kcal_per_day: baseKcalPerDay,
      kcal_per_kg: kcalPerKg,
      homemade_pct: homemadePct || 100,
      treats_pct: treatsPct || 0,
      mer: effectiveMer, // based on the 90% when treats are on
      full_mer: totalMer, // total daily kcal (100%)
      food_mer: foodMer,
      treat_mer: treatMer,
      scaling_factor: scalingFactor,
    };
  }

  /* ── Safe formula evaluator ───────────────────────── */
  function evalFormula(formula, scope) {
    if (!formula || !formula.trim()) {
      return scope.amount * scope.scaling_factor * scope.days;
    }
    try {
      const args = Object.keys(scope);
      const values = args.map(function (k) {
        return scope[k];
      });
      return new Function(args, "return (" + formula + ");").apply(
        null,
        values,
      );
    } catch (e) {
      console.warn("[my-recipes] Formula eval failed:", formula, e);
      return scope.amount * scope.scaling_factor * scope.days;
    }
  }

  /* ── Render ingredient rows ───────────────────────── */
  function renderRows(containerId, items, scope) {
    const container = document.getElementById(containerId);
    if (!container || !Array.isArray(items)) return;

    if (items.length === 0) {
      container.innerHTML = "";
      return;
    }

    container.innerHTML = items
      .map(function (item) {
        const rawAmount = parseFloat(item.amount || item.amount_per_day || 0);
        const unit = (item.unit || "g").toLowerCase();
        const itemScope = Object.assign({}, scope, { amount: rawAmount });
        const scaledGrams = evalFormula(item.formula || "", itemScope);

        var qtyStr;
        if (unit === "drop") {
          qtyStr = fmtQtyFromGrams(scaledGrams, unit);
        } else if (unit !== "g") {
          var roundedGrams = Math.round(scaledGrams);
          qtyStr =
            roundedGrams + " g (" + fmtQtyFromGrams(roundedGrams, unit) + ")";
        } else {
          qtyStr = fmtQtyFromGrams(scaledGrams, unit);
        }

        /* ── Build name + options chain ─────────────────── */

        function makeOption(name, link) {
          var safeName = String(name || "").trim();
          var safeLink = String(link || "").trim();
          if (!safeName) return "";
          if (safeLink) {
            return (
              '<a class="ingredient-row__name ingredient-row__name--link"' +
              ' href="' +
              safeLink +
              '"' +
              ' target="_blank" rel="noopener noreferrer">' +
              safeName +
              "</a>"
            );
          }
          return '<span class="ingredient-row__name">' + safeName + "</span>";
        }

        var orSep = '<span class="ingredient-row__or-sep"> or </span>';

        // Always start with the item's own name + link as the primary
        var primaryEl = makeOption(
          item.name,
          item.link && String(item.link).trim() ? item.link : "",
        );
        var parts = primaryEl ? [primaryEl] : [];

        // Append each option from supplement_options list
        var options = Array.isArray(item.options) ? item.options : [];
        options.forEach(function (opt) {
          var el = makeOption(opt.name, opt.link);
          if (el) parts.push(el);
        });

        var nameChain = parts.join(orSep);

        // Product link icon (top-level shopify product reference)
        var hasProductLink = !!(item.url && String(item.url).trim());
        var productLinkEl = hasProductLink
          ? '<a class="ingredient-row__product-link" href="' +
            item.url +
            '"' +
            ' target="_blank" rel="noopener noreferrer"' +
            ' aria-label="Open ingredient product in a new tab">' +
            '<span class="ingredient-row__product-link-icon" aria-hidden="true">&#8599;</span>' +
            "</a>"
          : "";

        return (
          '<div class="ingredient-row">' +
          '<span class="ingredient-row__name-wrap">' +
          '<span class="ingredient-row__name-stack ingredient-row__name-stack--chain">' +
          nameChain +
          "</span>" +
          productLinkEl +
          "</span>" +
          '<span class="ingredient-row__qty">' +
          qtyStr +
          "</span>" +
          "</div>"
        );
      })
      .join("");

    flashQtyCells(container.closest(".ingredient-table"));
  }

  /* ── Render total cost ────────────────────────────── */
  function renderTotalCost(recipeId, ingredients, supplements, scope, days) {
    const el = document.getElementById("cost-total-" + recipeId);
    if (!el) return;

    let total = 0;
    let hasAny = false;

    function addItems(items, multiplier) {
      if (!Array.isArray(items) || items.length === 0) return;
      items.forEach(function (item) {
        const rawAmount = parseFloat(item.amount || item.amount_per_day || 0);
        const itemScope = Object.assign({}, scope, { amount: rawAmount });
        const scaledGrams = evalFormula(item.formula || "", itemScope);
        const cost = costFromGrams(scaledGrams, item.cost_per_kg);
        if (cost == null || !Number.isFinite(cost)) return;
        total += cost * multiplier;
        hasAny = true;
      });
    }

    addItems(ingredients, 1);
    addItems(supplements, days);

    if (!hasAny) {
      el.innerHTML = "";
      return;
    }

    el.innerHTML =
      '<div class="ingredient-row ingredient-row--total" role="group" aria-label="Total cost">' +
      '<span class="ingredient-row__name-wrap">' +
      '<span class="ingredient-row__name ingredient-row__name--total">Estimated recipe cost for ' +
      days +
      " " +
      (days === 1 ? "day" : "days") +
      "</span>" +
      "</span>" +
      '<span class="ingredient-row__qty ingredient-row__qty--total">' +
      fmtGBP(total) +
      "</span>" +
      "</div>";
  }

  /* ── Render total feed per day ────────────────────── */
  function renderTotalPerDay(
    recipeId,
    items,
    supplements,
    scope,
    days,
    homemadePct,
    commercialKcal,
  ) {
    const el = document.getElementById("feed-total-" + recipeId);
    const commercialEl = document.getElementById(
      "commercial-total-" + recipeId,
    );
    if (!el) return;

    if (!Array.isArray(items) || items.length === 0) {
      el.innerHTML = "";
      if (commercialEl) commercialEl.innerHTML = "";
      return;
    }

    const fullMer = scope.food_mer;
    const kcalPerKg = parseFloat(scope && scope.kcal_per_kg);

    let homemadePerDayGrams = null;
    if (
      Number.isFinite(fullMer) &&
      Number.isFinite(kcalPerKg) &&
      kcalPerKg > 0
    ) {
      const homemadeKcalDay = fullMer * ((homemadePct || 100) / 100);
      homemadePerDayGrams = homemadeKcalDay / (kcalPerKg / 1000);
    }

    let supplementsPerDay = 0;
    if (Array.isArray(supplements) && supplements.length > 0) {
      let batchGrams = 0;
      supplements.forEach(function (item) {
        const rawAmount = parseFloat(item.amount || item.amount_per_day || 0);
        const itemScope = Object.assign({}, scope, { amount: rawAmount });
        const scaledGrams = evalFormula(item.formula || "", itemScope);
        if (typeof scaledGrams === "number" && Number.isFinite(scaledGrams))
          batchGrams += scaledGrams;
      });
      supplementsPerDay = days > 0 ? batchGrams / days : 0;
    }

    if (homemadePerDayGrams == null || !Number.isFinite(homemadePerDayGrams)) {
      let totalBatch = 0;
      items.forEach(function (item) {
        const rawAmount = parseFloat(item.amount || item.amount_per_day || 0);
        const itemScope = Object.assign({}, scope, { amount: rawAmount });
        const scaledGrams = evalFormula(item.formula || "", itemScope);
        if (typeof scaledGrams === "number" && Number.isFinite(scaledGrams))
          totalBatch += scaledGrams;
      });
      homemadePerDayGrams = days > 0 ? totalBatch / days : 0;
    }

    // const adjustedHomemade = Math.max(0, Math.round(homemadePerDayGrams - supplementsPerDay));
    const adjustedHomemade = Math.max(0, Math.round(homemadePerDayGrams));

    // el.innerHTML =
    //   '<div class="ingredient-row ingredient-row--total" role="group" aria-label="Total homemade amount per day">'
    //   + '<span class="ingredient-row__name-wrap">'
    //   + '<span class="ingredient-row__name ingredient-row__name--total">Homemade food per day</span>'
    //   + '</span>'
    //   + '<span class="ingredient-row__qty ingredient-row__qty--total">' + fmtGrams(adjustedHomemade) + '</span>'
    //   + '</div>';

    if (!commercialEl) return;

    if (homemadePct < 100 && Number.isFinite(fullMer) && commercialKcal > 0) {
      const commercialKcalDay = fullMer * ((100 - homemadePct) / 100);
      const commercialGramsDay = Math.round(
        (commercialKcalDay / commercialKcal) * 100,
      );

      commercialEl.innerHTML =
        '<div class="ingredient-row ingredient-row--total" role="group" aria-label="Total commercial amount per day">' +
        '<span class="ingredient-row__name-wrap">' +
        '<span class="ingredient-row__name ingredient-row__name--total">Commercial food per day</span>' +
        "</span>" +
        '<span class="ingredient-row__qty ingredient-row__qty--total">' +
        fmtGrams(commercialGramsDay) +
        "</span>" +
        "</div>";
    } else {
      commercialEl.innerHTML = "";
    }
  }

  function updateMerValue(card) {
    const calcEl = card.querySelector(".recipe-calculator");
    if (!calcEl) return;

    const baseKcalPerDay = parseFloat(calcEl.dataset.baseKcalPerDay) || 700;
    const kcalPerKg = parseFloat(calcEl.dataset.kcalPerKg);

    const weightInput = card.querySelector('[name="weight"]');
    const activityInput = card.querySelector('[name="activity"]');
    const dogWeight = parseFloat(weightInput ? weightInput.value : 0) || 0;
    const activityLevel =
      activityInput && activityInput.value ? String(activityInput.value) : "";
    const homemadePct = readFeedType(card) || 100;

    if (!dogWeight || dogWeight <= 0 || !activityLevel) return;

    const scope = buildScope(
      dogWeight,
      activityLevel,
      baseKcalPerDay,
      kcalPerKg,
      3,
      homemadePct,
      readTreatsPct(card),
    );
    renderMer(card, scope);
  }

  /* ── Recalculate and re-render a single card ──────── */
  function recalcCard(card) {
    const recipeId = card.dataset.recipeId;
    const calcEl = card.querySelector(".recipe-calculator");
    if (!calcEl) return;

    const baseKcalPerDay = parseFloat(calcEl.dataset.baseKcalPerDay) || 700;
    const kcalPerKg = parseFloat(calcEl.dataset.kcalPerKg);

    const weightInput = card.querySelector('[name="weight"]');
    const activityInput = card.querySelector('[name="activity"]');
    const daysInput = card.querySelector('[name="days"]');
    const dogWeight = parseFloat(weightInput ? weightInput.value : 0) || 0;
    const activityLevel =
      activityInput && activityInput.value
        ? String(activityInput.value)
        : "Less active / low energy";
    const days = parseInt(daysInput ? daysInput.value : 3, 10) || 3;
    const homemadePct = readFeedType(card);
    const commercialKcal = readCommercialKcal(card);

    if (!dogWeight || dogWeight <= 0) return;

    let ingredients = [];
    let supplements = [];

    try {
      ingredients = JSON.parse(calcEl.dataset.ingredients || "[]");
    } catch (e) {
      console.error("[my-recipes] Invalid ingredients JSON", e);
    }

    try {
      supplements = JSON.parse(calcEl.dataset.supplements || "[]");
    } catch (e) {
      console.error("[my-recipes] Invalid supplements JSON", e);
    }

    const scope = buildScope(
      dogWeight,
      activityLevel,
      baseKcalPerDay,
      kcalPerKg,
      days,
      homemadePct,
      readTreatsPct(card),
    );

    // MER display
    renderMer(card, scope);

    updateQtyHeader(recipeId, days);
    renderRows("ingredients-" + recipeId, ingredients, scope);
    renderTotalPerDay(
      recipeId,
      ingredients,
      supplements,
      scope,
      days,
      homemadePct,
      commercialKcal,
    );
    renderRows("supplements-" + recipeId, supplements, scope);
    renderTotalCost(recipeId, ingredients, supplements, scope, days);
  }

  /* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
     PROGRESSIVE DISCLOSURE
     State machine per card:
       step-1  → weight + activity shown
       step-2  → treats allowance question shown
       step-3  → MER value(s) + feed-type toggle shown
       step-4a → days slider shown (100% homemade)
       step-4b → commercial kcal shown (50/50), then days slider
       results → ingredient tables + totals + instructions shown
     ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

  /* Utility: add the rp-reveal transition class */
  function reveal(el) {
    if (!el) return;
    el.removeAttribute("hidden");
    el.classList.remove("hidden");
    el.classList.add("rp-reveal");
  }

  function hide(el) {
    if (!el) return;
    el.classList.add("hidden");
    el.classList.remove("rp-reveal");
  }

  /* Clear computed outputs when inputs are incomplete */
  function clearResults(card) {
    const recipeId = card.dataset.recipeId;

    // Clear rendered rows
    [
      "ingredients-",
      "supplements-",
      "feed-total-",
      "cost-total-",
      "commercial-total-",
    ].forEach(function (prefix) {
      const el = document.getElementById(prefix + recipeId);
      if (el) el.innerHTML = "";
    });

    // Restore loading placeholders
    const loadingEls = card.querySelectorAll(".ingredient-rows__loading");
    loadingEls.forEach(function (el) {
      el.style.display = "";
    });
  }

  function clearOutputs(card) {
    const merInput = card.querySelector('[name="mer"]');
    if (merInput) merInput.value = "";
    const treatInput = card.querySelector('[name="mer_treats"]');
    if (treatInput) treatInput.value = "";
    clearResults(card);
  }

  function clearFeedType(card) {
    const feedTypeBtns = card.querySelectorAll(".feed-type-btn");
    feedTypeBtns.forEach(function (btn) {
      btn.classList.remove("feed-type-btn--active");
      btn.setAttribute("aria-pressed", "false");
    });
  }

  function clearCommercialKcal(card) {
    const input = card.querySelector('[name="commercial_kcal"]');
    if (input) input.value = "";
  }

  function resetDaysField(card) {
    const daysInput = card.querySelector('[name="days"]');
    if (!daysInput) return;
    const defaultVal = daysInput.defaultValue || daysInput.min || 1;
    daysInput.value = defaultVal;
    const recipeId = card.dataset.recipeId;
    const daysOut = document.getElementById("days-out-" + recipeId);
    if (daysOut) daysOut.textContent = daysLabel(parseInt(daysInput.value, 10));
    if (card._syncDaysUI) {
      window.requestAnimationFrame(card._syncDaysUI);
    }
    updateSliderFill(daysInput);
  }

  /* Determine the current "step" for a card and show/hide accordingly */
  function syncSteps(card) {
    const recipeId = card.dataset.recipeId;
    const weightInput = card.querySelector('[name="weight"]');
    const activityInput = card.querySelector('[name="activity"]');
    const merField = card.querySelector(".calc-field--readonly"); // MER
    const feedTypeField = card.querySelector(".calc-field--feed-type");
    const commercialField = card.querySelector(".calc-field--commercial-kcal");
    const treatsField = card.querySelector(".calc-field--treats");
    const merTreatsField = card.querySelector(".calc-field--mer-treats");
    const daysField = (() => {
      const s = card.querySelector('[name="days"]');
      return s ? s.closest(".calc-field") : null;
    })();
    const commercialKcalInput = card.querySelector('[name="commercial_kcal"]');

    // Result sections
    const costCard =
      card.querySelector(
        ".feed-total-card:has(#cost-total-" + recipeId + ")",
      ) ||
      (() => {
        const el = document.getElementById("cost-total-" + recipeId);
        return el ? el.closest(".feed-total-card") : null;
      })();
    const ingredientTable = card.querySelector(
      '.ingredient-table[aria-label="Ingredient quantities"]',
    );
    const feedTotalCard =
      card.querySelector(
        ".feed-total-card:has(#feed-total-" + recipeId + ")",
      ) ||
      (() => {
        const el = document.getElementById("feed-total-" + recipeId);
        return el ? el.closest(".feed-total-card") : null;
      })();
    const commercialTotalCard =
      card.querySelector(
        ".feed-total-card:has(#commercial-total-" + recipeId + ")",
      ) ||
      (() => {
        const el = document.getElementById("commercial-total-" + recipeId);
        return el ? el.closest(".feed-total-card") : null;
      })();
    const supplementTable = card.querySelector(
      '.ingredient-table[aria-label="Supplement quantities"]',
    );
    const instructionsSection = card.querySelector(
      ".recipe-instructions-inline",
    );

    /* ── Step 1: Are weight + activity filled? ── */
    const dogWeight = parseFloat(weightInput ? weightInput.value : 0);
    const step1Done = !!(
      dogWeight &&
      dogWeight > 0 &&
      activityInput &&
      activityInput.value
    );

    if (!step1Done) {
      // Show only weight + activity; hide everything else
      if (merField) hide(merField);
      if (feedTypeField) hide(feedTypeField);
      if (commercialField) hide(commercialField);
      if (daysField) hide(daysField);
      if (treatsField) hide(treatsField);
      if (merTreatsField) hide(merTreatsField);
      clearTreats(card);
      clearFeedType(card);
      clearCommercialKcal(card);
      resetDaysField(card);
      hideResults(
        costCard,
        ingredientTable,
        feedTotalCard,
        commercialTotalCard,
        supplementTable,
        instructionsSection,
      );
      clearOutputs(card);
      return;
    }

    /* Step 1 done: ask about treats before showing MER */
    if (treatsField) reveal(treatsField);

    if (readTreats(card) === null) {
      if (merField) hide(merField);
      if (merTreatsField) hide(merTreatsField);
      if (feedTypeField) hide(feedTypeField);
      if (commercialField) hide(commercialField);
      if (daysField) hide(daysField);
      clearFeedType(card);
      clearCommercialKcal(card);
      resetDaysField(card);
      hideResults(
        costCard,
        ingredientTable,
        feedTotalCard,
        commercialTotalCard,
        supplementTable,
        instructionsSection,
      );
      clearOutputs(card);
      return;
    }

    /* Treats chosen: show MER breakdown, then feed-type toggle */
    updateMerValue(card);
    if (merField) reveal(merField);
    if (feedTypeField) reveal(feedTypeField);

    const homemadePct = readFeedType(card);
    const hasFeedType = homemadePct !== null;
    const is50 = homemadePct === 50;
    const commercialKcal = parseFloat(
      commercialKcalInput ? commercialKcalInput.value : 0,
    );
    const step2CommercialOk = !is50 || (is50 && commercialKcal > 0);

    if (!hasFeedType) {
      if (commercialField) hide(commercialField);
      if (daysField) hide(daysField);
      hideResults(
        costCard,
        ingredientTable,
        feedTotalCard,
        commercialTotalCard,
        supplementTable,
        instructionsSection,
      );
      clearResults(card);
      return;
    }

    if (is50) {
      if (commercialField) reveal(commercialField);
      if (daysField) hide(daysField);
      resetDaysField(card);

      if (!step2CommercialOk) {
        hideResults(
          costCard,
          ingredientTable,
          feedTotalCard,
          commercialTotalCard,
          supplementTable,
          instructionsSection,
        );
        clearResults(card);
        return;
      }

      if (daysField) reveal(daysField);
    } else {
      if (commercialField) hide(commercialField);
      clearCommercialKcal(card);
      if (daysField) reveal(daysField);
    }

    // Sync fill UI on reveal (width calculations need the element visible)
    if (card._syncDaysUI) {
      window.requestAnimationFrame(card._syncDaysUI);
    }

    /* ── Results ── */
    showResults(
      costCard,
      ingredientTable,
      feedTotalCard,
      commercialTotalCard,
      supplementTable,
      instructionsSection,
      is50,
    );
    recalcCard(card);
  }

  function hideResults(
    costCard,
    ingredientTable,
    feedTotalCard,
    commercialTotalCard,
    supplementTable,
    instructionsSection,
  ) {
    [
      costCard,
      ingredientTable,
      feedTotalCard,
      commercialTotalCard,
      supplementTable,
      instructionsSection,
    ].forEach(function (el) {
      if (el) hide(el);
    });
  }

  function showResults(
    costCard,
    ingredientTable,
    feedTotalCard,
    commercialTotalCard,
    supplementTable,
    instructionsSection,
    is50,
  ) {
    [
      costCard,
      ingredientTable,
      feedTotalCard,
      supplementTable,
      instructionsSection,
    ].forEach(function (el) {
      if (el) reveal(el);
    });
    // Show commercial total only in 50/50 mode
    if (commercialTotalCard) {
      if (is50) reveal(commercialTotalCard);
      else hide(commercialTotalCard);
    }
  }

  /* ── Activity helper text ─────────────────────────── */
  function syncActivityHelp(card) {
    const activityInput = card.querySelector('[name="activity"]');
    const help = card.querySelector("[data-activity-help]");
    if (!activityInput || !help) return;

    const val = String(activityInput.value || "").trim();
    const items = help.querySelectorAll("[data-activity]");
    items.forEach(function (el) {
      const isMatch =
        String(el.getAttribute("data-activity") || "").trim() === val;
      el.classList.toggle("is-active", isMatch);
      if (isMatch) el.removeAttribute("hidden");
      else el.setAttribute("hidden", "");
    });
  }

  /* ── Update slider track fill ─────────────────────── */
  function updateSliderFill(slider) {
    const min = parseFloat(slider.min) || 1;
    const max = parseFloat(slider.max) || 14;
    const value = parseFloat(slider.value) || 3;
    const ratio = (value - min) / (max - min);
    const trackW = slider.getBoundingClientRect().width || 0;
    const thumbTotal = 29;
    let pct;
    if (trackW > 1) {
      const thumbHalf = thumbTotal / 2;
      const thumbCenterPx = ratio * (trackW - thumbTotal) + thumbHalf;
      pct = (thumbCenterPx / trackW) * 100;
    } else {
      pct = ratio * 100;
    }
    if (!Number.isFinite(pct)) pct = 0;
    pct = Math.max(0, Math.min(100, pct));
    slider.style.background =
      "linear-gradient(to right, var(--rp-primary) 0%, var(--rp-primary) " +
      pct +
      "%, " +
      "rgba(var(--color-foreground, 0,0,0), 0.10) " +
      pct +
      "%, " +
      "rgba(var(--color-foreground, 0,0,0), 0.10) 100%)";
  }

  /* ── Macro nutrition chart ────────────────────────── */
  function toPctNumber(v) {
    if (v == null) return null;
    const s = String(v).trim();
    const m = s.match(/-?\d+(\.\d+)?/);
    if (!m) return null;
    const n = parseFloat(m[0]);
    return Number.isFinite(n) ? n : null;
  }

  let macroChartInstance = null;

  function initMacroChart(card) {
    const modal = document.getElementById("macro-info-modal");
    if (!modal) return;

    const wrap = modal.querySelector(".macro-breakdown");
    if (!wrap) return;

    const canvas = wrap.querySelector("canvas");
    if (!canvas) return;

    const rawEnergy = card.dataset.energy || "{}";

    let data;
    try {
      data = JSON.parse(rawEnergy);
    } catch (e) {
      console.warn("[my-recipes] Invalid nutrition JSON", e);
      return;
    }

    const protein = toPctNumber(data.Protein);
    const fat = toPctNumber(data.Fat);
    const carbs = toPctNumber(data.Carbohydrates);
    const fibre = toPctNumber(data.Fibre);

    const labels = [];
    const values = [];
    const colors = [];

    function add(label, value, color) {
      if (value == null || !Number.isFinite(value) || value <= 0) return;
      labels.push(label);
      values.push(value);
      colors.push(color);
    }

    add("Protein", protein, "#E53935");
    add("Fat", fat, "#6EC6FF");
    add("Carbs", carbs, "#FDD835");
    add("Fibre", fibre, "#43A047");

    if (values.length === 0) {
      console.warn("[my-recipes] No chart values found");
      return;
    }

    if (typeof window.Chart === "undefined") {
      console.warn("[my-recipes] Chart.js not loaded");
      return;
    }

    canvas.width = 280;
    canvas.height = 280;
    canvas.style.width = "280px";
    canvas.style.height = "280px";

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    if (macroChartInstance) {
      macroChartInstance.destroy();
    }

    macroChartInstance = new window.Chart(ctx, {
      type: "pie",
      data: {
        labels: labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderColor: "#ffffff",
            borderWidth: 2,
            hoverOffset: 6,
          },
        ],
      },
      options: {
        responsive: false,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "bottom",
          },
          tooltip: {
            callbacks: {
              label: function (c) {
                return c.label + ": " + c.parsed + "%";
              },
            },
          },
          datalabels: {
            color: "#ffffff",
            font: {
              weight: "bold",
              size: 14,
            },
            formatter: function (value) {
              return value + "%";
            },
          },
        },
      },
      plugins: [ChartDataLabels],
    });
  }

  /* ── Accordion open/close ─────────────────────────── */
  function openCard(card) {
    const btn = card.querySelector(".recipe-card__header");
    const body = card.querySelector(".recipe-card__body");
    if (!btn || !body) return;

    const isOpen = btn.getAttribute("aria-expanded") === "true";

    if (!isOpen) {
      body.removeAttribute("hidden");
      btn.setAttribute("aria-expanded", "true");
      card.classList.add("recipe-card--open");
      // Run initial step sync — this shows only step-1 fields
      syncSteps(card);
      initMacroChart(card);
      if (typeof card._syncDaysUI === "function") card._syncDaysUI();
    } else {
      body.setAttribute("hidden", "");
      btn.setAttribute("aria-expanded", "false");
      card.classList.remove("recipe-card--open");
    }
  }

  /* ── Wire up events on a card ─────────────────────── */
  function initCard(card) {
    const recipeId = card.dataset.recipeId;

    /* ── Pre-hide all step-gated sections ── */
    // These are hidden on init; syncSteps() reveals them progressively.
    var toHideOnInit = [
      card.querySelector(".calc-field--readonly"), // MER
      card.querySelector(".calc-field--feed-type"),
      card.querySelector(".calc-field--commercial-kcal"),
      card.querySelector(".calc-field--treats"),
      card.querySelector(".calc-field--mer-treats"),
      (() => {
        var s = card.querySelector('[name="days"]');
        return s ? s.closest(".calc-field") : null;
      })(),
      (() => {
        var el = document.getElementById("cost-total-" + recipeId);
        return el ? el.closest(".feed-total-card") : null;
      })(),
      card.querySelector(
        '.ingredient-table[aria-label="Ingredient quantities"]',
      ),
      (() => {
        var el = document.getElementById("feed-total-" + recipeId);
        return el ? el.closest(".feed-total-card") : null;
      })(),
      (() => {
        var el = document.getElementById("commercial-total-" + recipeId);
        return el ? el.closest(".feed-total-card") : null;
      })(),
      card.querySelector(
        '.ingredient-table[aria-label="Supplement quantities"]',
      ),
      card.querySelector(".recipe-instructions-inline"),
    ];
    toHideOnInit.forEach(function (el) {
      if (el) hide(el);
    });

    /* ── Header accordion ── */
    const headerBtn = card.querySelector(".recipe-card__header");
    if (headerBtn) {
      headerBtn.addEventListener("click", function () {
        openCard(card);
      });
    }

    /* ── Weight input ── */
    const weightInput = card.querySelector('[name="weight"]');
    if (weightInput) {
      weightInput.addEventListener("input", function () {
        syncSteps(card);
      });
    }

    /* ── Activity select ── */
    const activityInput = card.querySelector('[name="activity"]');
    if (activityInput) {
      syncActivityHelp(card);
      activityInput.addEventListener("change", function () {
        syncActivityHelp(card);
        syncSteps(card);
      });
    }

    /* ── Feed type toggle buttons ── */
    const feedTypeBtns = card.querySelectorAll(".feed-type-btn");
    feedTypeBtns.forEach(function (btn) {
      btn.addEventListener("click", function () {
        feedTypeBtns.forEach(function (b) {
          b.classList.remove("feed-type-btn--active");
          b.setAttribute("aria-pressed", "false");
        });
        btn.classList.add("feed-type-btn--active");
        btn.setAttribute("aria-pressed", "true");
        syncSteps(card);
      });
    });

    /* ── Treats toggle buttons ── */
    const treatsBtns = card.querySelectorAll(".treats-btn");
    treatsBtns.forEach(function (btn) {
      btn.addEventListener("click", function () {
        treatsBtns.forEach(function (b) {
          b.classList.remove("treats-btn--active");
          b.setAttribute("aria-pressed", "false");
        });
        btn.classList.add("treats-btn--active");
        btn.setAttribute("aria-pressed", "true");
        syncSteps(card);
      });
    });

    /* ── Commercial kcal input ── */
    const commercialKcalInput = card.querySelector('[name="commercial_kcal"]');
    if (commercialKcalInput) {
      commercialKcalInput.addEventListener("input", function () {
        syncSteps(card);
      });
    }

    /* ── Days slider ── */
    const daysSlider = card.querySelector('[name="days"]');
    if (daysSlider) {
      const daysOut = document.getElementById("days-out-" + recipeId);
      const ticks = card.querySelectorAll(".slider-ticks span[data-days]");
      const underlineFill = card.querySelector(".days-underline__fill");
      const thumbTotal = 29;

      function valueToThumbCenterPct(v) {
        const min = parseFloat(daysSlider.min) || 1;
        const max = parseFloat(daysSlider.max) || 14;
        const ratio = (v - min) / (max - min);
        const trackW = daysSlider.getBoundingClientRect().width || 0;
        if (trackW > 1) {
          const thumbHalf = thumbTotal / 2;
          const thumbCenterPx = ratio * (trackW - thumbTotal) + thumbHalf;
          return Math.max(0, Math.min(100, (thumbCenterPx / trackW) * 100));
        }
        return Math.max(0, Math.min(100, ratio * 100));
      }

      function syncDaysUI() {
        const currentVal = parseInt(daysSlider.value, 10) || 1;
        ticks.forEach(function (t) {
          const v = parseInt(t.dataset.days, 10);
          if (!Number.isFinite(v)) return;
          t.style.left = valueToThumbCenterPct(v) + "%";
        });
        if (underlineFill)
          underlineFill.style.width = valueToThumbCenterPct(currentVal) + "%";
      }

      card._syncDaysUI = syncDaysUI;

      updateSliderFill(daysSlider);
      syncDaysUI();

      daysSlider.addEventListener("input", function () {
        updateSliderFill(this);
        const v = parseInt(this.value, 10);
        if (underlineFill)
          underlineFill.style.width = valueToThumbCenterPct(v) + "%";
        if (daysOut) daysOut.textContent = daysLabel(v);
        recalcCard(card);
      });

      if (daysOut)
        daysOut.textContent = daysLabel(parseInt(daysSlider.value, 10));
    }

    syncSteps(card);
  }

  /* ── Tooltip positioning (keeps tips inside the calculator) ── */
  function initTooltips() {
    function position(wrap) {
      const tip = wrap.querySelector(".rp-tooltip");
      if (!tip) return;
      tip.style.setProperty("--tip-shift", "0px");

      const r = tip.getBoundingClientRect();
      const box = wrap.closest(".recipe-calculator");
      const b = box
        ? box.getBoundingClientRect()
        : { left: 0, right: window.innerWidth };
      const pad = 8;
      const minL = Math.max(b.left, 0) + pad;
      const maxR = Math.min(b.right, window.innerWidth) - pad;

      let shift = 0;
      if (r.left < minL) shift = minL - r.left;
      else if (r.right > maxR) shift = maxR - r.right;
      tip.style.setProperty("--tip-shift", shift + "px");
    }

    function handler(e) {
      const wrap =
        e.target && e.target.closest ? e.target.closest(".rp-info") : null;
      if (wrap) position(wrap);
    }
    document.addEventListener("mouseover", handler);
    document.addEventListener("focusin", handler);
  }

  /* ── Bootstrap ────────────────────────────────────── */
  function init() {
    initMacroInfoModal();
    initTooltips();
    document.querySelectorAll(".recipe-card").forEach(function (card) {
      initCard(card);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();